import React, { useState, useRef, useEffect, useCallback } from 'react';
import { searchEntities, type ExploreSearchResult } from '../../lib/exploreApi';

/** Map explore category → Neo4j label for seed_neighborhood. */
const CATEGORY_TO_LABEL: Record<string, string> = {
  actor: 'ThreatActor',
  family: 'MalwareFamily',
  ioc: 'IOC',
  cve: 'CVE',
  technique: 'AttackTechnique',
};

interface GraphSearchBarProps {
  onSeed: (name: string, label: string) => void;
  autoFocus?: boolean;
}

export function GraphSearchBar({ onSeed, autoFocus }: GraphSearchBarProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ExploreSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-focus on mount
  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const resp = await searchEntities({ query: q, limit: 10 });
      setResults(resp.results);
      setOpen(resp.results.length > 0);
      setActiveIndex(-1);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 250);
  }, [doSearch]);

  const handleSelect = useCallback((result: ExploreSearchResult) => {
    const label = CATEGORY_TO_LABEL[result.category] || 'MalwareFamily';
    setQuery(result.value);
    setOpen(false);
    onSeed(result.value, label);
  }, [onSeed]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!open || results.length === 0) {
      if (e.key === 'Enter' && query.trim()) {
        e.preventDefault();
        setOpen(false);
        // Default to family search
        onSeed(query.trim(), 'MalwareFamily');
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < results.length) {
        handleSelect(results[activeIndex]);
      } else if (query.trim()) {
        setOpen(false);
        onSeed(query.trim(), 'MalwareFamily');
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }, [open, results, activeIndex, query, handleSelect, onSeed]);

  const categoryLabel = (cat: string) => {
    switch (cat) {
      case 'actor': return 'Actor';
      case 'family': return 'Family';
      case 'ioc': return 'IOC';
      case 'cve': return 'CVE';
      case 'technique': return 'Technique';
      default: return cat;
    }
  };

  return (
    <div className="graph-search-bar" ref={containerRef}>
      <div className="graph-search-bar-input-wrap">
        <svg className="graph-search-bar-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="7" cy="7" r="4.5" />
          <line x1="10.5" y1="10.5" x2="14" y2="14" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="graph-search-bar-input"
          placeholder="Search entities (e.g. APT28, Emotet, CVE-2021-44228...)"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
        />
        {loading && <span className="graph-search-bar-spinner" />}
      </div>

      {open && results.length > 0 && (
        <ul className="graph-search-bar-dropdown">
          {results.map((r, i) => (
            <li
              key={`${r.category}-${r.value}`}
              className={`graph-search-bar-item${i === activeIndex ? ' active' : ''}`}
              onMouseDown={() => handleSelect(r)}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span className={`graph-search-bar-cat-badge graph-search-bar-cat-${r.category}`}>
                {categoryLabel(r.category)}
              </span>
              <span className="graph-search-bar-item-name">{r.value}</span>
              <span className="graph-search-bar-item-count">{r.doc_count} docs</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
